import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { PublicNav } from '../components/PublicNav';
import { Reveal } from '../components/Reveal';
import { ScrollTypewriter } from '../components/ScrollTypewriter';
import { Typewriter } from '../components/Typewriter';
import { Logo, ShieldOutline } from '../components/Brand';
import { MessageGregBubble } from '../components/MessageGregBubble';
import {
  Shield, Calendar, MessageSquare, CreditCard, Users, Award, ChevronRight,
  CheckCircle2, Dog, Percent, Handshake, Wallet, GraduationCap, ClipboardCheck,
  User, CalendarClock, Loader2, Monitor, Smartphone, Contact, Store,
  FileSignature, Calculator, Sparkles, Lock, Radio, Video, Ticket, ClipboardList,
} from 'lucide-react';
import { useAuth } from '../AuthContext';
import { trackCtaClick } from '../lib/analytics';

// -------------------------------------------------------------------------
// Performance: everything below the fold is code-split. The heavy pieces
// (Three-JS-adjacent LeadMap globe, marquee, workshop promo, SEO+FAQ tree)
// are loaded lazily so the initial home-page paint contains just the hero
// + typewriter + nav — the pieces the visitor sees on first paint. Each
// suspense fallback is a fixed-height skeleton that reserves layout space
// so we get zero Cumulative Layout Shift when the real component swaps in.
// -------------------------------------------------------------------------
const LeadMapSection      = lazy(() => import('../components/LeadMapSection').then(m => ({ default: m.LeadMapSection })));
const GlobalCoverageStrip = lazy(() => import('../components/GlobalCoverageStrip').then(m => ({ default: m.GlobalCoverageStrip })));
const BeTheFirstCallout   = lazy(() => import('../components/BeTheFirstCallout').then(m => ({ default: m.BeTheFirstCallout })));
const WorkshopPromoSection = lazy(() => import('../components/WorkshopPromoSection').then(m => ({ default: m.WorkshopPromoSection })));
const SEOFAQSection       = lazy(() => import('../components/SEOFAQSection').then(m => ({ default: m.SEOFAQSection })));
const SupportersMarquee   = lazy(() => import('../components/SupportersMarquee').then(m => ({ default: m.SupportersMarquee })));
const BlogSubscribe       = lazy(() => import('../components/BlogSubscribe').then(m => ({ default: m.BlogSubscribe })));

const SectionSkeleton = ({ minHeight = 320, label = 'Loading' }) => (
  <div
    className='w-full bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800'
    style={{ minHeight, contentVisibility: 'auto', containIntrinsicSize: `1px ${minHeight}px` }}
    aria-label={label}
    aria-busy='true'
  />
);


// ----------------------------------------------------------------------
// Animated K9os logo — the hero centerpiece. Floats, breathes, and
// spins twin orbital rings with a shimmer sweep. Fully on-brand,
// no external media, no video.
// ----------------------------------------------------------------------
const AnimatedLogo = () => {
  return (
    <Card
      className='relative overflow-hidden border border-slate-200 dark:border-slate-800 shadow-xl p-0'
      style={{
        aspectRatio: '16 / 10',
        background:
          'radial-gradient(circle at 50% 45%, #172554 0%, #0B1220 55%, #050810 100%)',
      }}
      data-testid='landing-hero-animated-logo'
    >
      {/* Subtle dot grid */}
      <div
        aria-hidden='true'
        className='absolute inset-0 opacity-25'
        style={{
          backgroundImage: 'radial-gradient(rgba(147,197,253,0.14) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Soft radial glow behind the logo */}
      <div
        aria-hidden='true'
        className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] aspect-square rounded-full pointer-events-none'
        style={{
          background:
            'radial-gradient(circle, rgba(200,16,46,0.35) 0%, rgba(27,58,135,0.18) 45%, rgba(27,58,135,0) 70%)',
        }}
      />

      {/* Twin rotating orbital rings */}
      <div className='absolute inset-0 flex items-center justify-center pointer-events-none'>
        <div className='relative w-[62%] aspect-square max-w-[360px]'>
          <div className='absolute inset-0 rounded-full border border-white/12 k9-spin-slow' />
          <div className='absolute inset-6 rounded-full border border-dashed border-[#93C5FD]/30 k9-spin-rev' />
          <div className='absolute inset-14 rounded-full border border-[#C8102E]/25 k9-spin-slow' style={{ animationDuration: '30s' }} />
        </div>
      </div>

      {/* Scan line sweep */}
      <div
        aria-hidden='true'
        className='k9-scan absolute inset-x-0 top-0 h-32 pointer-events-none'
        style={{ background: 'linear-gradient(to bottom, transparent, rgba(147,197,253,0.16), transparent)' }}
      />

      {/* Center logo with pulsing halo, floating, plus shimmer sweep across it */}
      <div className='absolute inset-0 flex items-center justify-center'>
        <div className='k9-logo-float relative'>
          <div className='k9-core-pulse rounded-full p-4 md:p-5 bg-gradient-to-br from-[#1B3A87]/40 via-[#0B1220]/60 to-[#C8102E]/30 ring-4 ring-white/10 backdrop-blur-sm'>
            <div className='relative overflow-hidden rounded-full w-40 h-40 md:w-52 md:h-52 flex items-center justify-center'>
              <img
                src='/img/k9os-logo-1024.png'
                alt='K9os logo'
                className='w-full h-full object-contain drop-shadow-[0_0_28px_rgba(200,16,46,0.45)]'
                data-testid='hero-animated-logo-img'
              />
              {/* Shimmer sweep that travels across the logo face */}
              <div
                aria-hidden='true'
                className='k9-shimmer absolute inset-0 pointer-events-none'
                style={{
                  background:
                    'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.28) 50%, transparent 70%)',
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const Feature = ({ icon: Icon, title, desc, testid }) => (
  <Card
    className='p-6 hover:shadow-lg transition-shadow border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col items-center text-center'
    data-testid={testid}
  >
    <div className='w-12 h-12 rounded-xl bg-[#C8102E]/10 dark:bg-[#C8102E]/20 text-[#C8102E] dark:text-[#F87171] flex items-center justify-center mb-4'>
      <Icon className='w-6 h-6' />
    </div>
    <h3 className='font-display font-semibold text-lg text-[#0B1220] dark:text-slate-50'>{title}</h3>
    <p className='text-sm text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed'>{desc}</p>
  </Card>
);

// ----------------------------------------------------------------------
// ScheduleDemoDialog — public lead-capture form. Posts to /api/demo-requests
// so trainer facilities can reach the K9os founder directly.
// ----------------------------------------------------------------------
const ScheduleDemoDialog = ({ trigger }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', facility_name: '',
    location: '', website: '', notes: '',
  });
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error('Please share your name and email so we can reach you.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/demo-requests', form);
      toast.success("You're on the list — Greg will reach out shortly.");
      setForm({ full_name: '', email: '', phone: '', facility_name: '', location: '', website: '', notes: '' });
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className='w-[calc(100%-1.5rem)] sm:w-full sm:max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl p-5 sm:p-6'
        data-testid='schedule-demo-dialog'
      >
        <DialogHeader>
          <DialogTitle className='font-display text-2xl'>Schedule a Free Demo</DialogTitle>
          <DialogDescription>
            See K9os in action. A 20-minute walkthrough tailored to your training facility — bookings, POS, memberships, contracts, and marketing under one login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className='space-y-3 mt-2'>
          <div>
            <Label htmlFor='demo-name'>Your full name *</Label>
            <Input id='demo-name' value={form.full_name} onChange={onChange('full_name')} placeholder='Jane Trainer' required data-testid='demo-input-name' />
          </div>
          <div className='grid sm:grid-cols-2 gap-3'>
            <div>
              <Label htmlFor='demo-email'>Email *</Label>
              <Input id='demo-email' type='email' value={form.email} onChange={onChange('email')} placeholder='you@yourfacility.com' required data-testid='demo-input-email' />
            </div>
            <div>
              <Label htmlFor='demo-phone'>Phone (optional)</Label>
              <Input id='demo-phone' type='tel' value={form.phone} onChange={onChange('phone')} placeholder='(555) 555-0123' data-testid='demo-input-phone' />
            </div>
          </div>
          <div>
            <Label htmlFor='demo-facility'>Facility / Business (optional)</Label>
            <Input id='demo-facility' value={form.facility_name} onChange={onChange('facility_name')} placeholder='Your K9 training business' data-testid='demo-input-facility' />
          </div>
          <div className='grid sm:grid-cols-2 gap-3'>
            <div>
              <Label htmlFor='demo-location'>City & State (optional)</Label>
              <Input id='demo-location' value={form.location} onChange={onChange('location')} placeholder='Austin, TX' data-testid='demo-input-location' />
            </div>
            <div>
              <Label htmlFor='demo-website'>Website or social link (optional)</Label>
              <Input id='demo-website' type='url' inputMode='url' value={form.website} onChange={onChange('website')} placeholder='https://yourfacility.com or @handle' data-testid='demo-input-website' />
            </div>
          </div>
          <div>
            <Label htmlFor='demo-notes'>What would you like to see? (optional)</Label>
            <Textarea id='demo-notes' rows={3} value={form.notes} onChange={onChange('notes')} placeholder='Bookings, memberships, POS, marketing…' data-testid='demo-input-notes' />
          </div>
          <DialogFooter className='mt-5 gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='ghost'
              onClick={() => setOpen(false)}
              disabled={submitting}
              className='w-full sm:w-auto'
              data-testid='demo-cancel-btn'
            >
              Cancel
            </Button>
            <Button
              type='submit'
              disabled={submitting}
              className='w-full sm:w-auto bg-[#C8102E] hover:bg-[#A50D26] text-white'
              data-testid='demo-submit-btn'
            >
              {submitting && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              {submitting ? 'Sending…' : 'Request a Demo'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Team = () => {
  const [members, setMembers] = useState([]);
  useEffect(() => {
    api.get('/team').then(r => setMembers(r.data)).catch(()=>{});
  }, []);
  if (members.length === 0) return null;
  return (
    <section id='team' className='py-16 bg-white dark:bg-slate-950'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='max-w-2xl text-center mx-auto'>
          <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E] mb-2'>K9os Origin</div>
          <h2 className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50'>Meet the Founder</h2>
          <p className='text-slate-600 dark:text-slate-300 mt-3'>The mind behind K9os — building tools that empower trainers with an all-in-one custom system to operate their small business or large training facility.</p>
        </div>
        <div className={`mt-10 grid gap-4 ${members.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
          {members.map((m) => (
            <Card key={m.id} className='p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-md transition-shadow text-center' data-testid={`team-member-${m.id}`}>
              {m.avatar_url ? (
                <img src={m.avatar_url} alt={m.full_name} loading='lazy' decoding='async' className='w-24 h-24 rounded-full object-cover mx-auto border-4 border-white dark:border-slate-800 shadow-md' />
              ) : (
                <div className='w-24 h-24 rounded-full bg-[#1B3A87] text-white mx-auto flex items-center justify-center font-display font-bold text-3xl'>{m.full_name[0]}</div>
              )}
              <div className='font-display font-bold text-lg text-[#0B1220] dark:text-slate-50 mt-4'>{m.full_name}</div>
              <div className='text-[11px] uppercase tracking-wider font-semibold text-[#1B3A87] dark:text-[#93C5FD] mt-0.5'>{m.title || m.role}</div>
              {m.bio && (
                <p
                  className='text-sm text-slate-600 dark:text-slate-300 mt-3 leading-relaxed'
                  data-testid={`team-member-bio-${m.id}`}
                >
                  {m.bio}
                </p>
              )}
              <div
                className='mt-4 inline-flex items-start gap-2 px-3 py-2 rounded-lg bg-[#C8102E]/8 dark:bg-[#C8102E]/15 border border-[#C8102E]/25 text-[13px] leading-snug font-semibold text-[#C8102E] dark:text-[#F87171]'
                data-testid={`team-member-tagline-${m.id}`}
              >
                <Award className='w-4 h-4 mt-[1px] shrink-0' />
                <span>Proven Dog Board &amp; Train Results &amp; Top Dog Trainer Recommendations.</span>
              </div>
              <ScheduleDemoDialog
                trigger={
                  <Button
                    size='lg'
                    className='k9-demo-cta mt-5 mx-auto flex bg-[#C8102E] hover:bg-[#A50D26] text-white h-14 px-6 text-base font-bold rounded-xl'
                    data-testid={`team-member-book-demo-${m.id}`}
                  >
                    <CalendarClock className='w-5 h-5 mr-2' />
                    Book a Demo
                  </Button>
                }
              />
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();

  // Route-specific SEO meta — ensures Google indexes the landing page with
  // its own strong title/description/canonical rather than falling back to
  // the static index.html defaults (which every SPA route otherwise shares).
  // Setting the canonical explicitly to https://k9os.app/ tells Google THIS
  // is the primary URL for the K9os brand — critical so /contact stops
  // winning the "k9os.app" search ranking after re-indexing.
  useEffect(() => {
    document.title = 'K9os App — All-in-One Dog Training Software for Dog Trainers';
    const setMeta = (selector, attr, content) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
        // rebuild attributes from the selector for freshly-created tags
        if (selector.includes('name=')) el.setAttribute('name', selector.match(/name="([^"]+)"/)[1]);
        if (selector.includes('property=')) el.setAttribute('property', selector.match(/property="([^"]+)"/)[1]);
        if (selector.includes('rel=')) el.setAttribute('rel', selector.match(/rel="([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, content);
    };
    setMeta('meta[name="description"]', 'content', 'K9os App is the all-in-one dog training software that replaces Square, Revelation, and stitched-together spreadsheets. Custom-built SEO website, kennel bookings, board & train onboarding, POS, memberships, contracts, messaging, push notifications, and marketing — unified for dog trainers and dog training companies.');
    setMeta('meta[property="og:title"]', 'content', 'K9os App — All-in-One Dog Training Software');
    setMeta('meta[property="og:url"]', 'content', 'https://k9os.app/');
    setMeta('link[rel="canonical"]', 'href', 'https://k9os.app/');
  }, []);

  // Legacy deep-link forwarder: the Board & Train / Memberships / Financing
  // sections now live on /example-website. Anyone hitting /#board-train,
  // /#memberships, or /#financing (from the old home-page collapsible or an
  // external inbound link) is redirected there so no history breaks. All
  // other hashes (workshop, team, comparison, etc.) still resolve on-page.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      if (['board-train', 'memberships', 'financing'].includes(id)) {
        navigate('/example-website#' + id, { replace: true });
        return;
      }
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [location.hash, navigate]);
  return (
    <div className='min-h-screen bg-[#FFF8F2] dark:bg-slate-950'>
      <PublicNav />

      {/*
        Screen-reader / SEO-only H1. Kept invisible via .sr-only so the
        existing hero visual is untouched, but crawlers (Google, Bing,
        GPTBot, PerplexityBot, ClaudeBot) get a single, keyword-rich H1
        that names the exact product category we're ranking for.
      */}
      <h1 className='sr-only' data-testid='seo-hero-h1'>
        K9os App — All-in-One Dog Training Software, CRM, Scheduler &amp; POS for Dog Trainers and Dog Training Companies in the USA
      </h1>

      {/* Hero */}
      <section className='relative hero-bg noise overflow-hidden'>
        {/* Minimal SaaS backdrop: soft brand-color aurora blobs only */}
        <div aria-hidden='true' className='hero-aurora hero-aurora--red' />
        <div aria-hidden='true' className='hero-aurora hero-aurora--blue' />
        <div aria-hidden='true' className='hero-aurora hero-aurora--gold' />
        {/* Embossed K9OS logo watermark centered behind the heading.
            Two variants: dark-navy silhouette for light hero, natural gold
            metallic for dark hero. Both use WebP with PNG fallback via
            <picture> and are swapped by CSS based on the `.dark` root class. */}
        <picture>
          <source type='image/webp' srcSet='/img/k9os-emblem-cropped-dark.webp' />
          <img
            src='/img/k9os-emblem-cropped-dark.png'
            alt=''
            aria-hidden='true'
            decoding='async'
            fetchPriority='high'
            className='hero-emboss-logo hero-emboss-logo--light pointer-events-none select-none'
            data-testid='hero-embossed-logo'
          />
        </picture>
        <picture>
          <source type='image/webp' srcSet='/img/k9os-emblem-cropped.webp' />
          <img
            src='/img/k9os-emblem-cropped.png'
            alt=''
            aria-hidden='true'
            decoding='async'
            fetchPriority='high'
            className='hero-emboss-logo hero-emboss-logo--dark pointer-events-none select-none'
          />
        </picture>
        <div className='relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center'>
          <Typewriter
            as='h1'
            className='font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50 leading-[1.05] whitespace-pre-line min-h-[3.2em]'
            speed={38}
            eraseSpeed={20}
            holdMs={2400}
            startDelay={200}
            sequences={[
              [
                { text: 'K9os',                                          className: 'jfk-crimson' },
                { text: ' can help you focus on ' },
                { text: 'training,',                                     className: 'text-[#1B3A87] dark:text-[#93C5FD]' },
                { text: ' not operations.' },
              ],
              [
                { text: 'K9os',                                          className: 'jfk-crimson' },
                { text: ' can integrate Square, Stripe, Paypal,\nGoogle Sheets ' },
                { text: '& more!',                                       className: 'text-[#1B3A87] dark:text-[#93C5FD]' },
              ],
            ]}
          />
          {/* Description — single tagline in the display font, italic,
              centered. Larger, more elegant treatment than plain body copy
              so it reads like a mission statement. Followed by the K9os
              signature line "One App. Endless Results." centered directly
              underneath as a supporting subtitle. */}
          <div
            className='mt-6 max-w-3xl mx-auto text-slate-800 dark:text-slate-100'
            data-testid='landing-hero-description'
          >
            <p className='font-display italic text-center text-xl sm:text-2xl md:text-3xl font-semibold leading-tight tracking-tight'>
              <span className='jfk-crimson'>K9os</span> is here to{' '}
              <span className='text-[#1B3A87] dark:text-[#93C5FD]'>REVOLUTIONIZE</span>{' '}
              the Dog Training Industry, one Dog Trainer at a Time.
            </p>
            <p
              className='mt-3 text-center font-display italic text-base sm:text-lg md:text-xl font-bold tracking-wide text-[#0B1220] dark:text-slate-50'
              data-testid='landing-hero-slogan'
            >
              One App. <span className='jfk-crimson'>Endless Results.</span>
            </p>
          </div>
          <div className='mt-8 flex flex-wrap gap-3 justify-center'>
            <ScheduleDemoDialog
              trigger={
                <Button
                  size='lg'
                  onClick={() => trackCtaClick('schedule_demo', 'hero')}
                  className='k9-demo-cta bg-[#C8102E] hover:bg-[#A50D26] text-white h-14 sm:h-16 px-7 sm:px-9 text-base sm:text-lg font-bold tracking-tight rounded-xl'
                  data-testid='landing-hero-schedule-demo-cta'
                >
                  <CalendarClock className='w-5 h-5 sm:w-6 sm:h-6 mr-2' />
                  Schedule a Free Demo
                </Button>
              }
            />
          </div>
          <div className='mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-700 dark:text-slate-200 justify-center'>
            {['24/7 Customer Support', 'Stripe + PayPal Integrations', 'Custom Websites', 'Revolutionary Technology'].map((t) => (
              <div key={t} className='flex items-center gap-1.5'><CheckCircle2 className='w-4 h-4 text-[#167A5A]' /> {t}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Trainer Reach Map — USA + Canada (right under the main hero).
          Wrapped in a single Suspense so the map, coverage strip, and
          workshop promo all stream in together without blocking the hero. */}
      <Reveal direction='up' delay={100}>
        <Suspense fallback={<SectionSkeleton minHeight={640} label='Loading map…' />}>
          <LeadMapSection />

          {/* First-mover promo — sits between the trainer map and the
              coverage strip so visitors see the "be the first in your
              state / region" hook right after seeing the geographic
              context. Copy explicitly avoids implying adoption. */}
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6'>
            <BeTheFirstCallout
              scopeLabel='state or region'
              ctaLabel='Claim your area first'
              testid='landing-be-the-first'
            />
          </div>

          {/* Global coverage strip — sits directly under the map so
              visitors see the "who we serve" callout with USA states,
              Canada, and Worldwide framing right after the trainer map. */}
          <GlobalCoverageStrip />

          {/* Workshop promo — sits directly beneath the map so visitors who
              just saw the reach section land on the "Register Today" CTA. */}
          <WorkshopPromoSection />
        </Suspense>
      </Reveal>

      {/* SEO — "Built for Dog Trainers" / Who It's For + FAQ
          (crawlable, keyword-rich). Placed ABOVE the "Everything in one
          platform" features grid so visitors — and Google/Bing/GPT
          crawlers — see the audience framing before the feature list. */}
      <Reveal direction='up' delay={60}>
        <Suspense fallback={<SectionSkeleton minHeight={480} label='Loading FAQ…' />}>
          <SEOFAQSection />
        </Suspense>
      </Reveal>

      {/* Features grid */}
      <Reveal direction='left'>
        <section id='programs' className='py-16 bg-white dark:bg-slate-950 border-y border-slate-200 dark:border-slate-800'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Centered heading block + scroll-triggered typewriter on the H2. */}
          <div className='max-w-3xl mx-auto text-center'>
            <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E] mb-2'>Everything in one platform</div>
            <ScrollTypewriter
              as='h2'
              className='font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0B1220] dark:text-slate-50'
              speed={22}
              segments={[{ text: 'Easy to Use. Client & Dog Profiles. Better Communication. Custom created for all Trainers or large Facilities.' }]}
            />
            <p className='text-slate-600 dark:text-slate-300 mt-3'>The all-in-one operating system for working-dog facilities &mdash; bookings, POS, contracts, and marketing under one login. Easy to onboard new Clients &amp; communication. Custom App &amp; Website. SEO &amp; Keyword Optimized. Google Ads Ready.</p>
          </div>
          <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10'>
            <Reveal direction='left'  delay={0}>   <Feature icon={Calendar}      title='Live Boarding Calendar' desc='Multi-view scheduler with run-grid matrix, drag-and-drop reschedule, vaccination compliance.' testid='feature-calendar' /></Reveal>
            <Reveal direction='right' delay={80}>  <Feature icon={CreditCard}    title='Native Stripe + PayPal' desc='No third-party overlap. Lower fees. Bookings, POS, memberships — all reconciled in one ledger.' testid='feature-payments' /></Reveal>
            <Reveal direction='left'  delay={160}> <Feature icon={Dog}           title='Dog Profiles + VAX' desc='High-res photos, behavioral notes, training needs, vaccination uploads with expiry tracking.' testid='feature-profiles' /></Reveal>
            <Reveal direction='right' delay={240}> <Feature icon={Users}         title='Multi-Role Access' desc='Clients, Trainers, Front Desk, Admin — granular permissions on a single secure login.' testid='feature-roles' /></Reveal>
            <Reveal direction='left'  delay={0}>   <Feature icon={MessageSquare} title='Group Chat & Team Chat' desc='Universal community chat with role badges plus a Slack-style team channel for staff.' testid='feature-messaging' /></Reveal>
            <Reveal direction='right' delay={80}>  <Feature icon={Award}         title='VIP Membership' desc='Built-in recurring billing. Maximize lifetime value with monthly subscriptions.' testid='feature-membership' /></Reveal>
            <Reveal direction='left'  delay={160}> <Feature icon={Shield}        title='POS Terminal' desc='Ring up retail, grooming, training packages on the same Stripe rail. Square-replacement.' testid='feature-pos' /></Reveal>
            <Reveal direction='right' delay={240}> <Feature icon={Award}         title='Success Timeline' desc='Public client transformations — a marketing flywheel for non-clients to convert.' testid='feature-timeline' /></Reveal>
            <Reveal direction='left'  delay={0}>   <Feature icon={FileSignature} title='Digital Waivers & Contracts' desc='Board & train agreements, liability waivers, and vaccination releases — e-signed on any device and stored to each dog’s file.' testid='feature-waivers' /></Reveal>
            <Reveal direction='right' delay={80}>  <Feature icon={Calculator}    title='Complete Accounting Records' desc='Every booking, POS sale, membership charge, and refund written to one ledger. Export tax-ready reports and reconcile without spreadsheets.' testid='feature-accounting' /></Reveal>
            {/* NEW: Professional Dog Business React App Website — surfaces why
                the K9os-built site matters, not just as a marketing brochure
                but as the front door that feeds bookings, memberships, POS,
                and CRM into the same login the trainer uses every day. */}
            <Reveal direction='left'  delay={160}> <Feature icon={Monitor}       title='Professional Dog Business React App Website' desc='A custom SEO-optimized React app website that IS your K9os front door — every booking, contract, membership sign-up, and message flows straight into the same login you already use. No plugin patchwork, no third-party redirects, no lost leads.' testid='feature-website' /></Reveal>
          </div>
        </div>
      </section>
      </Reveal>

      {/* ────────────────────────────────────────────────
          Online Education Suite — section for trainers who teach online.
          Promotes the new LMS, Live Events (ticketed), and 1-on-1 Peer
          Video features, and routes power users to the dedicated
          /services page for the full pitch. Placed directly after the
          general features grid so visitors who scrolled past "boarding +
          POS" immediately see the new revenue streams.
          ──────────────────────────────────────────────── */}
      <Reveal direction='up'>
      <section
        id='online-education'
        className='relative overflow-hidden py-16 sm:py-20 text-white'
        style={{ background: 'radial-gradient(1200px 500px at 20% 0%, #172554 0%, #0B1220 55%, #050810 100%)' }}
        data-testid='home-online-education-section'
      >
        <div
          aria-hidden='true'
          className='absolute inset-0 opacity-25'
          style={{ backgroundImage: 'radial-gradient(rgba(147,197,253,0.14) 1px, transparent 1px)', backgroundSize: '24px 24px' }}
        />
        <div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='max-w-3xl mx-auto text-center mb-10'>
            <Badge className='bg-[#C8102E] text-white mb-3'><Sparkles className='w-3 h-3 mr-1' /> New — Online education suite</Badge>
            <h2 className='font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight'>
              Teach dog training online — <span className='text-[#F59E0B]'>without the SaaS stack.</span>
            </h2>
            <p className='mt-4 text-slate-200 text-base sm:text-lg leading-relaxed'>
              Run on-demand courses, sell tickets to live webinars, and book private
              1-on-1 video consults — all on the same K9os login your bookings and POS
              already live on. No per-student fees, no third-party keys.
            </p>
          </div>

          <div className='grid md:grid-cols-3 gap-5'>
            {/* Online Courses */}
            <Card className='relative overflow-hidden p-6 bg-white/[0.04] border border-white/10 backdrop-blur-sm text-white hover:-translate-y-0.5 transition-transform' data-testid='home-edu-card-courses'>
              <div aria-hidden='true' className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#1B3A87] to-[#93C5FD]' />
              <div className='w-12 h-12 rounded-xl bg-[#1B3A87] flex items-center justify-center mb-4 shadow-md'>
                <GraduationCap className='w-6 h-6' />
              </div>
              <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171]'>LMS · On-demand</div>
              <h3 className='font-display text-xl font-bold mt-1 leading-tight'>Online Courses</h3>
              <p className='text-sm text-slate-200 mt-2 leading-relaxed'>
                Upload lesson videos, attach PDFs, and design fillable worksheets or printable homework.
                Students work through it from their own portal with auto-save drafts.
              </p>
              <ul className='mt-4 space-y-1.5 text-xs text-slate-200'>
                <li className='flex items-center gap-2'><ClipboardList className='w-3.5 h-3.5 text-emerald-300' /> Fillable + printable worksheets</li>
                <li className='flex items-center gap-2'><Users className='w-3.5 h-3.5 text-emerald-300' /> Hand-pick enrolled students</li>
                <li className='flex items-center gap-2'><CheckCircle2 className='w-3.5 h-3.5 text-emerald-300' /> Per-student progress tracking</li>
              </ul>
            </Card>

            {/* Live Events */}
            <Card className='relative overflow-hidden p-6 bg-white/[0.04] border border-white/10 backdrop-blur-sm text-white hover:-translate-y-0.5 transition-transform' data-testid='home-edu-card-live-events'>
              <div aria-hidden='true' className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#C8102E] to-[#F59E0B]' />
              <div className='w-12 h-12 rounded-xl bg-[#C8102E] flex items-center justify-center mb-4 shadow-md'>
                <Radio className='w-6 h-6' />
              </div>
              <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171]'>Live streaming · Ticketed</div>
              <h3 className='font-display text-xl font-bold mt-1 leading-tight'>Live Events & Webinars</h3>
              <p className='text-sm text-slate-200 mt-2 leading-relaxed'>
                Run paid masterclasses or Q&A nights. Sell tickets at your price, cap attendance,
                and pin up to 6 POS products to a live-stream merch sidebar.
              </p>
              <ul className='mt-4 space-y-1.5 text-xs text-slate-200'>
                <li className='flex items-center gap-2'><Ticket className='w-3.5 h-3.5 text-emerald-300' /> PayPal-powered ticketing</li>
                <li className='flex items-center gap-2'><Lock className='w-3.5 h-3.5 text-emerald-300' /> Access-code gated stream</li>
                <li className='flex items-center gap-2'><Sparkles className='w-3.5 h-3.5 text-emerald-300' /> Shoppable merch sidebar</li>
              </ul>
            </Card>

            {/* Peer Video */}
            <Card className='relative overflow-hidden p-6 bg-white/[0.04] border border-white/10 backdrop-blur-sm text-white hover:-translate-y-0.5 transition-transform' data-testid='home-edu-card-video-calls'>
              <div aria-hidden='true' className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#167A5A] to-[#34D399]' />
              <div className='w-12 h-12 rounded-xl bg-[#167A5A] flex items-center justify-center mb-4 shadow-md'>
                <Video className='w-6 h-6' />
              </div>
              <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#F87171]'>Peer coaching · Private</div>
              <h3 className='font-display text-xl font-bold mt-1 leading-tight'>1-on-1 Peer Video</h3>
              <p className='text-sm text-slate-200 mt-2 leading-relaxed'>
                Book private online consults or small-group sessions with your clients.
                No accounts, no downloads — the link shows up on their dashboard.
              </p>
              <ul className='mt-4 space-y-1.5 text-xs text-slate-200'>
                <li className='flex items-center gap-2'><Video className='w-3.5 h-3.5 text-emerald-300' /> HD 1-on-1 and group calls</li>
                <li className='flex items-center gap-2'><Shield className='w-3.5 h-3.5 text-emerald-300' /> Unguessable private rooms</li>
                <li className='flex items-center gap-2'><CreditCard className='w-3.5 h-3.5 text-emerald-300' /> Charge on your own Stripe/PayPal</li>
              </ul>
            </Card>
          </div>

          {/* CTA row */}
          <div className='mt-10 flex flex-col sm:flex-row items-center justify-center gap-3'>
            <Link
              to='/services'
              onClick={() => trackCtaClick('services', 'home_online_education')}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all'
              data-testid='home-edu-services-cta'
            >
              Explore the education suite <ChevronRight className='w-4 h-4' />
            </Link>
            <Link
              to='/pricing'
              onClick={() => trackCtaClick('pricing', 'home_online_education')}
              className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white hover:bg-white/20 font-bold text-sm transition-all backdrop-blur-sm'
              data-testid='home-edu-pricing-cta'
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>
      </Reveal>

      {/* Custom-built promise — mirror of the section on the Pricing page.
          Sits directly ABOVE the "Example of a Website" section on the home
          page and drives visitors to /pricing for full package details.
          Every trainer gets their own K9os app — this is the key
          differentiator we want in visitors' minds before they see the
          example demo. */}
      <Reveal direction='up'>
        <section
          className='py-14 sm:py-20 bg-white dark:bg-[#0B1220] border-t border-slate-200 dark:border-slate-800'
          data-testid='home-custom-built-promise'
        >
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
            <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-3'>
              <Sparkles className='w-3.5 h-3.5' /> Custom-built promise
            </div>
            <h2 className='font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight'>
              No two K9os apps are the same.
            </h2>
            <p className='mt-4 text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed'>
              Unlike Gingr, Time to Pet, or Kennel Connection &mdash; where every customer gets the same shared SaaS &mdash; every K9os trainer gets their own custom-built app connected to their own Stripe or PayPal account, their own integrations (Twilio SMS, Google Business, Zapier, custom APIs), and their own workflow. White-label ready. If it&rsquo;s not on this list and you need it, we build it.
            </p>
            <div className='mt-8 flex flex-col sm:flex-row items-center justify-center gap-3'>
              <Link
                to='/pricing'
                onClick={() => trackCtaClick('pricing', 'home_custom_built_section')}
                className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all'
                data-testid='home-custom-built-pricing-cta'
              >
                See pricing &amp; packages <ChevronRight className='w-4 h-4' />
              </Link>
              <Link
                to='/contact'
                onClick={() => trackCtaClick('book_a_demo', 'home_custom_built_section')}
                className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 text-[#0B1220] dark:text-slate-100 hover:border-[#1B3A87] hover:bg-slate-50 dark:hover:bg-slate-700 font-bold text-sm transition-all'
                data-testid='home-custom-built-demo-cta'
              >
                Book a free scoping call
              </Link>
            </div>
          </div>
        </section>
      </Reveal>

      {/* ─────────────────────────────────────────────
          Example Website teaser card: the Board & Train / Memberships /
          Financing demo used to live inline here as a collapsible on the
          home page. It now lives at /example-website — this card is the
          click-through that carries visitors to that dedicated page.
          Meet the Founder still lives outside this card, further down.
          ───────────────────────────────────────────── */}
      <Reveal direction='right'>
      <section className='py-10 bg-gradient-to-b from-white to-slate-50 dark:from-slate-950 dark:to-slate-900'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
          <Link
            to='/example-website'
            className='group relative block w-full rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all overflow-hidden'
            data-testid='home-example-website-cta'
          >
            {/* Subtle gradient bar */}
            <div aria-hidden='true' className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#C8102E] via-[#1B3A87] to-[#F59E0B]' />
            <div className='px-6 py-6 sm:px-8 sm:py-7 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 text-center sm:text-left'>
              {/* Icon cluster */}
              <div className='flex items-center gap-2 shrink-0'>
                {[Monitor, Smartphone, Contact, Store].map((Icon, i) => (
                  <div
                    key={i}
                    className='w-10 h-10 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800 text-[#1B3A87] dark:text-[#93C5FD]'
                  >
                    <Icon className='w-5 h-5' />
                  </div>
                ))}
              </div>
              <div className='flex-1'>
                <div className='text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E]'>Example of a Website</div>
                <h3 className='font-display text-2xl md:text-3xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight mt-1'>
                  A website, APP, CRM &amp; POS &middot; Custom Dog Boarding System &mdash; all in one!
                </h3>
                <p className='text-slate-600 dark:text-slate-300 mt-2 text-sm md:text-base'>
                  <span className='font-semibold text-[#1B3A87] dark:text-[#93C5FD]'>Open the live example website</span>{' '}
                  and see how facilities book, sell memberships, and finance training right on the same site.
                </p>
              </div>
              {/* Chevron */}
              <div className='shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-[#C8102E] text-white shadow-md group-hover:scale-105 group-hover:translate-x-0.5 transition-transform'>
                <ChevronRight className='w-5 h-5' />
              </div>
            </div>
          </Link>
        </div>
      </section>
      </Reveal>

      {/* K9os + Competition Comparison — quick preview card + CTA to /compare.
          Sits ABOVE the K9os Origin / Team section on the home page so
          visitors get one last conversion hook before the "meet the founder"
          story. Interlinks the home page directly into the SEO comparison
          hub for maximum PageRank flow. */}
      <Reveal direction='up'>
        <section
          id='comparison'
          className='py-16 sm:py-20 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'
          data-testid='home-comparison-section'
        >
          <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
            <div className='max-w-3xl mx-auto text-center'>
              <div className='inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-[#C8102E] mb-3'>
                <Award className='w-3.5 h-3.5' /> K9os + Competition Comparison
              </div>
              <h2 className='font-display text-3xl sm:text-4xl md:text-5xl font-bold text-[#0B1220] dark:text-slate-50 leading-tight tracking-tight'>
                How does K9os stack up against Gingr, Time to Pet, Square &amp; the rest?
              </h2>
              <p className='mt-4 text-slate-600 dark:text-slate-300 text-base sm:text-lg leading-relaxed'>
                We benchmarked K9os head-to-head against 15 major dog-training and pet-business software platforms &mdash; scheduling, CRM, POS, memberships, contracts, messaging, and marketing. See where K9os wins and every competitor falls short.
              </p>
            </div>

            {/* Compact horizontal bar chart — teaser of the full /compare
                chart. Shows K9os leading a short list of top competitors so
                visitors get instant proof + click through for the full view. */}
            <Card className='mt-10 p-6 sm:p-8 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm' data-testid='home-comparison-chart'>
              <div className='space-y-2.5 max-w-3xl mx-auto'>
                {[
                  { name: 'K9os App', score: 9.4, benchmark: true },
                  { name: 'Gingr', score: 7.8 },
                  { name: 'PetExec', score: 7.6 },
                  { name: 'Revelation Pets', score: 7.5 },
                  { name: 'Square', score: 7.4 },
                  { name: 'Time to Pet', score: 7.2 },
                  { name: 'DoTimely', score: 6.8 },
                ].map((row) => (
                  <div key={row.name} className='flex items-center gap-3'>
                    <div className={`w-36 sm:w-44 shrink-0 text-sm ${row.benchmark ? 'font-bold text-[#0B1220] dark:text-slate-50' : 'text-slate-700 dark:text-slate-300'}`}>
                      {row.benchmark ? (
                        <span className='inline-flex items-center gap-1.5'><Award className='w-3.5 h-3.5 text-[#C8102E]' /> {row.name}</span>
                      ) : (
                        row.name
                      )}
                    </div>
                    <div className='flex-1 h-7 sm:h-8 bg-slate-100 dark:bg-slate-800 rounded-md overflow-hidden'>
                      <div
                        className={`h-full rounded-md flex items-center justify-end pr-3 text-[11px] font-bold text-white shadow-inner ${
                          row.benchmark
                            ? 'bg-gradient-to-r from-[#1B3A87] via-[#1B3A87] to-[#C8102E]'
                            : 'bg-slate-400 dark:bg-slate-600'
                        }`}
                        style={{ width: `${(row.score / 10) * 100}%` }}
                      >
                        {row.score.toFixed(1)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA row + supporting note */}
              <div className='mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 pt-6 border-t border-slate-100 dark:border-slate-800'>
                <p className='text-sm text-slate-500 dark:text-slate-400 text-center sm:text-left'>
                  Full 15-competitor feature matrix, category filters, and detailed breakdowns for every rival.
                </p>
                <Link
                  to='/compare'
                  onClick={() => trackCtaClick('compare', 'home_comparison_section')}
                  className='inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#C8102E] hover:bg-[#A50D26] text-white font-bold text-sm shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap'
                  data-testid='home-comparison-cta'
                >
                  See the full comparison <ChevronRight className='w-4 h-4' />
                </Link>
              </div>
            </Card>
          </div>
        </section>
      </Reveal>

      {/* Team section (always visible — stays outside the collapsible group) */}
      <Reveal direction='up'>
        <Team />
      </Reveal>

      {/* Newsletter opt-in placed just above the supporter marquee. Same
          "Stay in touch" component used on /blog and its posts — visitors
          coming from the home page get a natural CTA to receive the
          weekly SEO article roundup right before they scroll into the
          social-proof strip. Own `source` string tags analytics + scopes
          the DOM id so it can coexist with the /blog instance. */}
      <Reveal direction='up' delay={40}>
        <section className='py-10 bg-white dark:bg-slate-950' data-testid='landing-blog-subscribe-section'>
          <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
            <Suspense fallback={<SectionSkeleton minHeight={140} label='Loading newsletter opt-in…' />}>
              <BlogSubscribe source='landing-marquee' />
            </Suspense>
          </div>
        </section>
      </Reveal>

      {/* Supported Trainers & Companies logo marquee above the footer */}
      <Reveal direction='up' delay={80}>
        <Suspense fallback={<SectionSkeleton minHeight={140} label='Loading partners…' />}>
          <SupportersMarquee />
        </Suspense>
      </Reveal>

      {/* Footer */}
      <footer className='py-10 bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row gap-4 md:gap-6 items-center justify-between text-center md:text-left'>
          <Logo />
          <div className='flex flex-col md:items-end gap-1'>
            <div className='text-xs text-slate-500 dark:text-slate-400'>© {new Date().getFullYear()} K9os.app · All rights reserved.</div>
            <div className='text-xs text-slate-500 dark:text-slate-400' data-testid='footer-credit'>
              Website Created by{' '}
              <a
                href='https://www.GregMiceli.com'
                target='_blank'
                rel='noopener noreferrer'
                className='font-semibold text-[#1B3A87] dark:text-[#93C5FD] hover:text-[#C8102E] dark:hover:text-[#F87171] underline-offset-2 hover:underline transition-colors'
              >
                www.GregMiceli.com
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating quick-chat bubble — available site-wide from every public page */}
      <MessageGregBubble />
    </div>
  );
}
